import { queryOptions } from '@tanstack/react-query'
import { createInsightsReadinessError, insightsReadinessSchema } from '@testfeed/shopify-contracts'
import { type InsightsRenderResponse, parseInsightsRenderResponse } from '../insightsRenderContract'
import { InsightsFailure } from '../insightsFailure'
import { reportInsightsFailure, renderIssuesDiagnostic } from '../insightsDiagnostics'
import { insightsLoadingCopy as copy } from '@testfeed/product-language'
import { shopifyApiFetch, type ShopifyTokenSource } from '../shopifyApiFetch'
import { clearInsightsAttemptHistory, runInsightsAttempt } from '../insightsAttempt'
import { initializeInsightsEvidence } from '../insightsEvidence'
import { waitForShopifyOperation } from '../shopifyRequestLifecycle'
import {
  buildShopifyAppScopeKey,
  getVerifiedShopifyScope,
  shopifyQueryKey,
  type ShopifyAppContextValue,
} from '../ShopifyAppContext'

/** Per-scope query resource, not React render state. Only an explicit recheck resets its budget. */
export class InsightsPolling {
  startedAt = 0
  requests = 0
  reset() {
    this.startedAt = 0
    this.requests = 0
  }
}
const POLL_WINDOW_MS = 10 * 60_000
const POLL_INTERVAL_MS = 30_000
const MAX_READS = 21

export function insightsPollingActive(polling: InsightsPolling, scheduled = true) {
  return (
    Boolean(polling.startedAt) &&
    polling.requests < MAX_READS &&
    Date.now() - polling.startedAt <= POLL_WINDOW_MS - (scheduled ? POLL_INTERVAL_MS : 0)
  )
}

export function insightsQueryOptions(
  shopify: ShopifyTokenSource,
  scope: ShopifyAppContextValue,
  polling: InsightsPolling,
) {
  const scopeKey = buildShopifyAppScopeKey(scope)
  const isActive = () => !scope.isActiveScope || scope.isActiveScope(scopeKey)
  const enabled = Boolean(
    getVerifiedShopifyScope(scope.shopDomain) && scope.orgId?.trim() && scope.connectionId?.trim() && isActive(),
  )
  void initializeInsightsEvidence(enabled ? scopeKey : undefined, clearInsightsAttemptHistory)
  return queryOptions<InsightsRenderResponse>({
    queryKey: shopifyQueryKey(scope.shopDomain, scope.orgId, scope.connectionId, ['insights']),
    enabled,
    retry: false,
    networkMode: 'always',
    staleTime: (query) => (query.state.data?.state === 'preparing' ? 0 : 60_000),
    gcTime: 5 * 60_000,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    refetchIntervalInBackground: false,
    refetchInterval: (query) => {
      if (
        query.state.status !== 'success' ||
        query.state.data?.state !== 'preparing' ||
        !isActive() ||
        !insightsPollingActive(polling)
      )
        return false
      return POLL_INTERVAL_MS
    },
    queryFn: async ({ signal, client, queryKey }): Promise<InsightsRenderResponse> => {
      if (!enabled || !isActive()) throw new Error(copy.error)
      // A paused background interval may fire after the wall-clock limit. Never issue that late request.
      const current = client.getQueryData<InsightsRenderResponse>(queryKey)
      if (current?.state === 'preparing' && polling.startedAt && !insightsPollingActive(polling, false)) return current
      if (!polling.startedAt) polling.startedAt = Date.now()
      polling.requests++
      return runInsightsAttempt(
        signal,
        async (attempt) => {
          const response = await shopifyApiFetch(shopify, '/api/insights', {
            init: { signal: attempt.signal, headers: { 'X-Correlation-Id': attempt.metadata().attemptId } },
            onPhase: (phase) => {
              if (!isActive()) throw new InsightsFailure('cancelled', 'obsolete_scope', 'scope')
              attempt.phase(phase)
            },
          })
          attempt.response(response.status, response.headers)
          if (!isActive()) throw new InsightsFailure('cancelled', 'obsolete_scope', 'scope')
          if (response.status === 401 || response.status === 403)
            return createInsightsReadinessError({ state: 'blocked', reason: 'access' }, attempt.metadata().attemptId)
              .body
          attempt.phase('response_parse')
          let payload: unknown
          try {
            payload = await waitForShopifyOperation(attempt.signal, () => response.json())
          } catch (error) {
            if (attempt.signal.aborted) throw error
            if (!response.ok) throw new InsightsFailure('http', 'http_status', 'response')
            if (error instanceof SyntaxError) throw new InsightsFailure('json_decode', 'invalid_json', 'response')
            throw new InsightsFailure('network', 'body_read_failed', 'response')
          }
          attempt.phase('scope_validation')
          if (!isActive()) throw new InsightsFailure('cancelled', 'obsolete_scope', 'scope')
          if (!response.ok) {
            const readiness = insightsReadinessSchema.safeParse(payload)
            if (!readiness.success) throw new InsightsFailure('http', 'http_status', 'response')
            const expected = createInsightsReadinessError(readiness.data)
            if (response.status !== expected.status)
              throw new InsightsFailure('http', 'readiness_status_mismatch', 'response')
            return readiness.data
          }
          const data = parseInsightsRenderResponse(payload, {
            shopDomain: scope.shopDomain!,
            orgId: scope.orgId!,
            connectionId: scope.connectionId!,
          })
          if (!isActive()) throw new InsightsFailure('cancelled', 'obsolete_scope', 'scope')
          polling.startedAt = 0
          polling.requests = 0
          if (data.state === 'ready') {
            const diagnostic = renderIssuesDiagnostic(data.renderIssues, attempt.metadata())
            if (diagnostic)
              reportInsightsFailure(
                shopify,
                scopeKey,
                {
                  ...attempt.metadata(),
                  phase: 'response_parse',
                  outcome: 'error',
                  phases: [],
                  failureClass: 'render_contract',
                },
                diagnostic,
              )
          }
          return { ...data, diagnosticAttempt: { ...attempt.metadata(), scopeKey } }
        },
        (record) => {
          if (!isActive()) return
          // Reporting uses the same auth path: bounded best effort, never a
          // prerequisite for the error UI or a replacement for browser evidence.
          reportInsightsFailure(shopify, scopeKey, record)
        },
      )
    },
  })
}
